import { useEffect, useState } from "react";
import { useShareTrip } from "@/lib/api/hooks";
import type { Trip } from "@/lib/api/schemas";

interface ShareDialogProps {
  trip: Trip;
  onClose: () => void;
}

export function ShareDialog({ trip, onClose }: ShareDialogProps) {
  const share = useShareTrip(trip.id);
  const [token, setToken] = useState<string | null>(
    trip.shareToken ?? null
  );
  const [copied, setCopied] = useState(false);

  // Auto-generate a share token on open if none exists yet.
  useEffect(() => {
    if (!token && !share.isPending) {
      share.mutate(undefined, {
        onSuccess: (res) => setToken(res.shareToken),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const url = token ? `${window.location.origin}/shared/${token}` : "";
  const tripLabel =
    trip.title || trip.destination || "my travel plan";
  const shareText = `Check out ${tripLabel} on roam:`;

  const encoded = {
    url: encodeURIComponent(url),
    text: encodeURIComponent(shareText),
    combined: encodeURIComponent(`${shareText} ${url}`),
  };

  const copyToClipboard = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for environments without clipboard API permissions.
      const tmp = document.createElement("textarea");
      tmp.value = url;
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand("copy");
      document.body.removeChild(tmp);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const nativeShare = async () => {
    if (!url || !navigator.share) return;
    try {
      await navigator.share({
        title: `roam — ${tripLabel}`,
        text: shareText,
        url,
      });
    } catch {
      // User cancelled — no-op.
    }
  };

  const hasNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-dialog-title"
    >
      <div
        className="absolute inset-0 bg-deep/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-md rounded-2xl frost-panel shadow-2xl page-fade"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <h2
              id="share-dialog-title"
              className="text-lg font-semibold text-ink"
            >
              Share this trip
            </h2>
            <p className="text-xs text-ink-light mt-0.5">
              Anyone with the link can view your itinerary (read-only).
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-9 h-9 rounded-full bg-deep/60 border border-white/20 text-ink hover:bg-deep/80"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-4">
          {share.isError && (
            <p className="text-sm text-red-300 bg-red-400/10 rounded-lg px-3 py-2">
              Couldn't generate a share link. Try again in a moment.
            </p>
          )}

          {/* URL + copy */}
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={url || "Generating link..."}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 min-w-0 rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <button
              onClick={copyToClipboard}
              disabled={!url}
              className="shrink-0 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-deep hover:bg-accent-light disabled:opacity-40"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>

          {/* Share destinations */}
          <div>
            <p className="text-xs uppercase tracking-wider text-ink-light/80 font-semibold mb-2">
              Share directly
            </p>
            <div className="grid grid-cols-2 gap-2">
              {hasNativeShare && (
                <ShareBtn
                  disabled={!url}
                  onClick={nativeShare}
                  label="Share…"
                  icon="📱"
                />
              )}
              <ShareBtn
                disabled={!url}
                href={`mailto:?subject=${encoded.text}&body=${encoded.combined}`}
                label="Email"
                icon="✉️"
              />
              <ShareBtn
                disabled={!url}
                href={`https://wa.me/?text=${encoded.combined}`}
                label="WhatsApp"
                icon="💬"
              />
              <ShareBtn
                disabled={!url}
                href={`https://twitter.com/intent/tweet?text=${encoded.text}&url=${encoded.url}`}
                label="X / Twitter"
                icon="𝕏"
              />
              <ShareBtn
                disabled={!url}
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encoded.url}`}
                label="LinkedIn"
                icon="in"
              />
              <ShareBtn
                disabled={!url}
                href={`https://telegram.me/share/url?url=${encoded.url}&text=${encoded.text}`}
                label="Telegram"
                icon="✈"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShareBtn({
  label,
  icon,
  href,
  onClick,
  disabled,
}: {
  label: string;
  icon: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const className =
    "inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-ink hover:bg-white/10 hover:border-white/30 transition-colors disabled:opacity-40 disabled:pointer-events-none";

  if (href) {
    return (
      <a
        href={disabled ? undefined : href}
        target="_blank"
        rel="noopener noreferrer"
        aria-disabled={disabled}
        className={className}
      >
        <span aria-hidden="true" className="text-base w-5 text-center">
          {icon}
        </span>
        <span className="truncate">{label}</span>
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={className}
    >
      <span aria-hidden="true" className="text-base w-5 text-center">
        {icon}
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}
